import { defineStore } from 'pinia'
import FirebaseInstance from '@/services/FirebaseInstance.js'
import AxiosInstance from '@/services/AxiosInstance.js'
import Logger from '@/services/Logger.js'
import { DeepgramService } from '@/services/DeepgramService'

const logger = new Logger('ServiceHealthStore', true)

const MIN_STATUS_DELAY_MS = 700
const FIREBASE_MIN_EVENT_DELAY_MS = 1500
const FIREBASE_REALTIME_TEST_PATH = 'firewallTests'
const SERVICE_CHECK_TIMEOUT_MS = 2000

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const ensureMinimumStatusDelay = async (startedAt: number) => {
  const elapsed = Date.now() - startedAt
  if (elapsed < MIN_STATUS_DELAY_MS) {
    await delay(MIN_STATUS_DELAY_MS - elapsed)
  }
}

export function clearFirebaseFailure() {
  if (typeof window === 'undefined') return
  localStorage.removeItem('firebase:previous_websocket_failure')
}


export const useServiceHealthStore = defineStore('serviceHealth', {
  state: () => {
    return {
      deepgramStatus: 'unknown' as 'unknown' | 'healthy' | 'unhealthy',
      firebaseStatus: 'unknown' as 'unknown' | 'healthy' | 'unhealthy',
      apiStatus: 'unknown' as 'unknown' | 'healthy' | 'unhealthy',
      s3Status: 'unknown' as 'unknown' | 'healthy' | 'unhealthy',
      lastCheckedAt: null as number | null,
      hasInitialRun: false,
      failureOverrideEnabled: false,
      isRunningChecks: false
    }
  },
  getters: {
    hasFailures(state): boolean {
      return (
        state.deepgramStatus === 'unhealthy' ||
        state.firebaseStatus === 'unhealthy' ||
        state.apiStatus === 'unhealthy' ||
        state.s3Status === 'unhealthy'
      )
    }
  },

  actions: {
    async runChecks() {
      this.isRunningChecks = true
      try {
        this.catchFirebaseFailure()
        await Promise.all([
          this.checkFirebaseAccess(),
          this.checkApiAccess(),
          this.checkDeepgramAccess(),
          this.checkS3Access()
        ])
        this.lastCheckedAt = Date.now()
        this.hasInitialRun = true
        if (!this.hasFailures) {
          this.failureOverrideEnabled = false
        }
      } finally {
        this.isRunningChecks = false
      }
    },

    setFailureOverride(isEnabled: boolean) {
      this.failureOverrideEnabled = isEnabled
    },

    catchFirebaseFailure() {
      if (typeof window === 'undefined') return

      const previousWebsocketFailure = localStorage.getItem('firebase:previous_websocket_failure')
      if (previousWebsocketFailure) {
        logger.warn('Firebase websocket failure detected, attempting to reconnect...')
        clearFirebaseFailure()
      }
    },

    async checkS3Access() {
      const startedAt = Date.now()

      let isSuccess = false
      try {
        const s3AssetUrl = `${_ASSETS_BUCKET_URL_}/health-check.json`
        const responseData = await AxiosInstance.get(s3AssetUrl)

        if (responseData && responseData.success === true) {
          isSuccess = true
        }
      } catch (err) {
        // Error is logged by the final status update
        isSuccess = false
      }

      // Wait for the minimum delay to complete
      await ensureMinimumStatusDelay(startedAt)

      // Now, set the final status
      if (isSuccess) {
        this.s3Status = 'healthy'
      } else {
        this.s3Status = 'unhealthy'
        logger.error(
          'S3 access check failed: health-check.json content was invalid or unreachable.'
        )
      }
    },

    async checkFirebaseAccess() {
      const startedAt = Date.now()
      this.firebaseStatus = 'unknown'
      try {
        await this.verifyFirebaseVersionRead()
        await this.verifyFirebaseRealtimeListener()
        this.firebaseStatus = 'healthy'
      } catch (error) {
        this.firebaseStatus = 'unhealthy'
        clearFirebaseFailure()
        logger.error('Firebase database url blocked or inaccessible:', error)
      } finally {
        await ensureMinimumStatusDelay(startedAt)
      }
    },

    async checkApiAccess() {
      this.apiStatus = 'unknown'

      const controller = new AbortController()
      const timeoutId = setTimeout(
        () => controller.abort('api_check_timeout'),
        SERVICE_CHECK_TIMEOUT_MS
      )

      try {
        await AxiosInstance.get(`${_API_BASE_URL_}/v2/users/test-access`, {
          signal: controller.signal
        })
        this.apiStatus = 'healthy'
      } catch (err: any) {
        this.apiStatus = 'unhealthy'
        logger.error('Mable API access checks failed.', err)
      } finally {
        clearTimeout(timeoutId)
      }
    },

    async verifyFirebaseRealtimeListener() {
      const testPath = `${FIREBASE_REALTIME_TEST_PATH}/${Date.now()}`
      const firebaseRef = FirebaseInstance.get().child(testPath)
      const eventDelay = delay(FIREBASE_MIN_EVENT_DELAY_MS)

      return new Promise<void>((resolve, reject) => {
        let timeoutId: ReturnType<typeof setTimeout>
        let settled = false
        let valueHandler: (() => Promise<void>) | null = null

        const cleanup = () => {
          if (settled) return
          settled = true
          try {
            if (valueHandler) {
              firebaseRef.off('value', valueHandler as any)
            }
            void firebaseRef.remove()
          } catch (error) {
            logger.warn('Error cleaning up Firebase realtime test', error)
          }
        }

        const finishWithError = (error: unknown) => {
          cleanup()
          reject(error instanceof Error ? error : new Error(String(error)))
        }

        timeoutId = setTimeout(() => {
          finishWithError(new Error('firebase_websocket_timeout'))
        }, SERVICE_CHECK_TIMEOUT_MS)

        valueHandler = async () => {
          try {
            clearTimeout(timeoutId)
            await eventDelay
            cleanup()
            resolve()
          } catch (error) {
            finishWithError(error)
          }
        }

        firebaseRef.on('value', valueHandler, (error: unknown) => {
          clearTimeout(timeoutId)
          finishWithError(error)
        })

        firebaseRef.set({ ping: Date.now() }).catch((error: unknown) => {
          clearTimeout(timeoutId)
          finishWithError(error)
        })
      })
    },

    async verifyFirebaseVersionRead() {
      const versionRef = FirebaseInstance.get().child('version')

      return new Promise<void>((resolve, reject) => {
        let timeoutId: ReturnType<typeof setTimeout>

        const valueCallback = () => {
          clearTimeout(timeoutId)
          resolve()
        }

        const errorCallback = (error: unknown) => {
          clearTimeout(timeoutId)
          reject(error)
        }

        timeoutId = setTimeout(() => {
          versionRef.off('value', valueCallback) // Detach the listener
          reject(new Error('firebase_version_timeout'))
        }, SERVICE_CHECK_TIMEOUT_MS)

        versionRef.once('value', valueCallback, errorCallback)
      })
    },

    async checkDeepgramAccess() {
      const deepgramService = new DeepgramService()
      const startedAt = Date.now()
      this.deepgramStatus = 'unknown'

      try {
        const isHealthy = await deepgramService.checkConnection()
        this.deepgramStatus = isHealthy ? 'healthy' : 'unhealthy'
        if (isHealthy) {
        } else {
          const detail = 'Connection could not be established.'
          logger.error('Deepgram access checks failed:', detail)
        }
      } catch (error) {
        logger.error('Deepgram access checks failed:', error)
        this.deepgramStatus = 'unhealthy'
      } finally {
        await ensureMinimumStatusDelay(startedAt)
      }
    }
  }
})
